import BaseComponent from 'foundation/base-component';
import confetti from '@hiseb/confetti/dist/index.js';
import PropTypes from 'prop-types';
import React from 'react';
import reactDOM from 'react-dom';

const SEGMENT_COUNT = 6;
const SEGMENT_ANGLE = 360 / SEGMENT_COUNT;
const FULL_ROTATIONS = 5;
const MIN_LANDING_OFFSET = 8;
const MAX_LANDING_OFFSET = 18;
const WINNER_SEGMENTS = [0, 2, 4];
const NON_WINNER_SEGMENTS = [1, 3, 5];
const SEGMENTS = [
    { id: 'winner-one', index: 0 },
    { id: 'try-again-one', index: 1 },
    { id: 'winner-two', index: 2 },
    { id: 'try-again-two', index: 3 },
    { id: 'winner-three', index: 4 },
    { id: 'try-again-three', index: 5 }
];

const toBoolean = (value) => value === true || value === 'true';

const polarToCartesian = (angle, radius) => {
    const radians = angle * Math.PI / 180;

    return {
        x: 100 + (radius * Math.cos(radians)),
        y: 100 + (radius * Math.sin(radians))
    };
};

const createSegmentPath = (index) => {
    const startAngle = -120 + (index * SEGMENT_ANGLE);
    const endAngle = startAngle + SEGMENT_ANGLE;
    const start = polarToCartesian(startAngle, 96);
    const end = polarToCartesian(endAngle, 96);

    return `M 100 100 L ${start.x} ${start.y} A 96 96 0 0 1 ${end.x} ${end.y} Z`;
};

const getRandomItem = (items) => items[Math.floor(Math.random() * items.length)];

const getLandingOffset = () => {
    const offsetRange = MAX_LANDING_OFFSET - MIN_LANDING_OFFSET;
    const offset = MIN_LANDING_OFFSET + (Math.random() * offsetRange);

    return Math.random() < 0.5 ? -offset : offset;
};

const renderSegment = (segment) => {
    const { id, index } = segment;
    const isWinnerSegment = WINNER_SEGMENTS.indexOf(index) !== -1;
    const labelPosition = polarToCartesian(-90 + (index * SEGMENT_ANGLE), 62);
    const segmentClassName = isWinnerSegment ?
        'nature-trail-wheel__segment nature-trail-wheel__segment--winner' :
        'nature-trail-wheel__segment nature-trail-wheel__segment--try-again';
    const labelClassName = isWinnerSegment ?
        'nature-trail-wheel__segment-label nature-trail-wheel__segment-label--winner' :
        'nature-trail-wheel__segment-label';

    return (
        <g key={id}>
            <path className={segmentClassName} d={createSegmentPath(index)} />
            <text
                className={labelClassName}
                transform={`rotate(${index * SEGMENT_ANGLE} ${labelPosition.x} ${labelPosition.y})`}
                x={labelPosition.x}
                y={labelPosition.y}
                textAnchor="middle">
                {isWinnerSegment ? (
                    <tspan x={labelPosition.x} dy="0.35em">WINNER</tspan>
                ) : (
                    <React.Fragment>
                        <tspan x={labelPosition.x} dy="-0.65em">BETTER LUCK</tspan>
                        <tspan x={labelPosition.x} dy="1.2em">NEXT TIME</tspan>
                    </React.Fragment>
                )}
            </text>
        </g>
    );
};

class NatureTrailWheel extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            hasSpun: false,
            isSpinning: false,
            result: null,
            rotation: 0
        };

        this.wheelButton = React.createRef();
        this.handleSpin = this.handleSpin.bind(this);
        this.handleSpinEnd = this.handleSpinEnd.bind(this);
    }

    launchWinnerConfetti() {
        const wheelButton = this.wheelButton.current;
        const prefersReducedMotion = window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!wheelButton || prefersReducedMotion) {
            return;
        }

        const wheelBounds = wheelButton.getBoundingClientRect();

        confetti({
            position: {
                x: wheelBounds.left + (wheelBounds.width / 2),
                y: wheelBounds.top + (wheelBounds.height / 2)
            },
            count: 120,
            size: 1.2,
            velocity: 180,
            fade: true
        });
    }

    handleSpin() {
        const { isDisabled, isWinner } = this.props;
        const { hasSpun, isSpinning } = this.state;

        if (isDisabled || isSpinning || hasSpun) {
            return;
        }

        const possibleSegments = isWinner ? WINNER_SEGMENTS : NON_WINNER_SEGMENTS;
        const targetSegment = getRandomItem(possibleSegments);
        const rotation = (FULL_ROTATIONS * 360) -
            (targetSegment * SEGMENT_ANGLE) + getLandingOffset();

        this.setState({
            isSpinning: true,
            rotation
        });
    }

    handleSpinEnd(event) {
        const { isWinner } = this.props;
        const { isSpinning } = this.state;

        if (!isSpinning || event.target !== event.currentTarget || event.propertyName !== 'transform') {
            return;
        }

        if (isWinner) {
            this.launchWinnerConfetti();
        }

        this.setState({
            hasSpun: true,
            isSpinning: false,
            result: isWinner
        });
    }

    render() {
        const { isDisabled } = this.props;
        const {
            hasSpun, isSpinning, result, rotation
        } = this.state;
        const isButtonDisabled = isDisabled || isSpinning || hasSpun;
        const wrapperClassName = isDisabled ?
            'nature-trail-wheel nature-trail-wheel--disabled' :
            'nature-trail-wheel';

        let statusMessage = 'Click the wheel to spin';

        if (isDisabled) {
            statusMessage = 'Complete the previous steps to unlock the wheel';
        } else if (isSpinning) {
            statusMessage = 'The wheel is spinning';
        } else if (result !== null) {
            statusMessage = result ? 'Winner!' : 'Better luck next time';
        }

        return (
            <div className={wrapperClassName}>
                <button
                    aria-label={statusMessage}
                    className="nature-trail-wheel__button"
                    disabled={isButtonDisabled}
                    onClick={this.handleSpin}
                    ref={this.wheelButton}
                    type="button">
                    <span className="nature-trail-wheel__pointer" />
                    <span
                        className="nature-trail-wheel__disc"
                        onTransitionEnd={this.handleSpinEnd}
                        style={{ transform: `rotate(${rotation}deg)` }}>
                        <svg
                            aria-hidden="true"
                            className="nature-trail-wheel__graphic"
                            focusable="false"
                            viewBox="0 0 200 200">
                            {SEGMENTS.map(renderSegment)}
                            <circle className="nature-trail-wheel__hub" cx="100" cy="100" r="18" />
                        </svg>
                    </span>
                </button>

                <p aria-live="polite" className="nature-trail-wheel__status">
                    {statusMessage}
                </p>

            </div>
        );
    }
}

NatureTrailWheel.propTypes = {
    isDisabled: PropTypes.bool,
    isWinner: PropTypes.bool
};

NatureTrailWheel.defaultProps = {
    isDisabled: false,
    isWinner: false
};

class NatureTrailWheelComponent extends BaseComponent {
    constructor(...args) {
        super(...args);

        this.render();
        this.scrollToOnMobile();
    }

    scrollToOnMobile() {
        if (toBoolean(this.options.isDisabled) || window.innerWidth > 768) {
            return;
        }

        this.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    render() {
        reactDOM.render(
            <NatureTrailWheel
                isDisabled={toBoolean(this.options.isDisabled)}
                isWinner={toBoolean(this.options.isWinner)} />,
            this.element,
        );
    }

    onDestroy() {
        reactDOM.unmountComponentAtNode(this.element);
    }
}

export default NatureTrailWheelComponent;
